import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Hook for scroll-triggered fade-in animations using IntersectionObserver
 */
export const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: options.threshold || 0.15, rootMargin: options.rootMargin || '0px 0px -40px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin]);

  return [ref, isVisible];
};

/**
 * Parallax background component - works on desktop and mobile
 * Renders a fixed background image that shifts subtly on scroll
 */
export const ParallaxBg = ({ imageClass, children, className = '', speed = 0.3 }) => {
  const containerRef = useRef(null);
  const bgRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const bg = bgRef.current;
    if (!container || !bg) return;

    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          const rect = container.getBoundingClientRect();
          const scrolled = -rect.top;
          const offset = scrolled * speed;
          bg.style.transform = `translate3d(0, ${offset}px, 0) scale(1.15)`;
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return (
    <div ref={containerRef} className={`parallax-container ${className}`}>
      <div ref={bgRef} className={`parallax-bg ${imageClass}`} />
      <div className="parallax-overlay" />
      <div className="parallax-content">
        {children}
      </div>
    </div>
  );
};

/**
 * Wrapper component for scroll-reveal animations
 */
export const ScrollReveal = ({ children, delay = 0, direction = 'up', className = '' }) => {
  const [ref, isVisible] = useScrollReveal();

  const directionClass = {
    up: 'reveal-up',
    left: 'reveal-left',
    right: 'reveal-right',
    scale: 'reveal-scale',
  }[direction] || 'reveal-up';

  return (
    <div
      ref={ref}
      className={`${directionClass} ${isVisible ? 'revealed' : ''} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};
